import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  IconButton,
  icons,
  Menu,
  MenuItem,
  theme,
  Tooltip,
} from '@ublend-npm/aulaui-next';
import { useSlateStatic, ReactEditor } from 'slate-react';
import { toggleTextColourMark } from '../../../lib/commands';
import { TooltipTitle } from '../../HotkeyLabel/HotkeyLabel.styled';
import { HotkeyLabels } from '../../HotkeyLabel/HotkeyLabel';
import { AulaRange } from '../../../types';
import { hasSelection, getTextColour } from '../../../lib/queries';
import { matchesHotkey, Hotkey } from '../../../lib/hotkeys';
import { ColourIcon } from './TextColourButton.styled';
import { TextColourKey, textColours } from '../../../plugins/richtext/colours';

type ColorButtonProps = {
  handleTargetClick: (event: React.SyntheticEvent) => void;
  isOpen: boolean;
  hide: () => void;
};

export const TextColourButton = React.forwardRef(
  (
    {
      lastActiveSelection,
      label,
      hotkeys,
    }: {
      lastActiveSelection: AulaRange;
      label: string;
      hotkeys: Hotkey[];
    },
    ref,
  ) => {
    const editor = useSlateStatic();
    const [showTooltip, setShowTooltip] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { FontColor } = icons;
    const selectedColour = getTextColour(editor);

    useEffect(() => {
      const onkeydown = (e: KeyboardEvent) => {
        const hotkeyCommand = hotkeys.some((hotkey) =>
          matchesHotkey(hotkey, e),
        );
        if (
          hotkeyCommand &&
          hasSelection(editor) &&
          ReactEditor.isFocused(editor)
        ) {
          e.preventDefault();
          buttonRef.current?.click();
          const el: HTMLElement | null = document.querySelector(
            `[aria-label="${selectedColour}"]`,
          );
          el?.focus();
        }
      };
      document.addEventListener('keydown', onkeydown);
      return () => {
        document.removeEventListener('keydown', onkeydown);
      };
    }, [editor, selectedColour, hotkeys]);

    const tooltip = (
      <TooltipTitle>
        {label}
        {hotkeys && <HotkeyLabels hotkeys={hotkeys} />}
      </TooltipTitle>
    );

    const onSelectColour = useCallback(
      (colour: TextColourKey) =>
        (event: MouseEvent<HTMLButtonElement> | React.KeyboardEvent) => {
          event.preventDefault();
          event.stopPropagation();
          toggleTextColourMark(editor, colour, lastActiveSelection);
        },
      [editor, lastActiveSelection],
    );

    const renderMenuItems = ({ getItemProps }: any) =>
      (Object.keys(textColours) as TextColourKey[]).map((colour) => (
        <MenuItem
          key={colour}
          size="small"
          leftDecoration={<ColourIcon colour={textColours[colour]} />}
          primaryText={colour}
          {...getItemProps({
            item: () => {},
            onClick: onSelectColour(colour),
          })}
          attributes={{
            'aria-label': colour,
            selected: colour === selectedColour,
          }}
        />
      ));

    const renderButton = ({
      handleTargetClick,
      isOpen,
      hide,
    }: ColorButtonProps) => (
      <Tooltip title={tooltip} open={showTooltip}>
        <div
          // Fix for Safari issue where the heading menu closes immediately on click
          onPointerDown={(event: React.SyntheticEvent) =>
            event.preventDefault()
          }
        >
          <IconButton
            label="Select font colour"
            icon={() => (
              <FontColor
                svgProps={{
                  underlinedColor: selectedColour
                    ? textColours[selectedColour]
                    : 'transparent',
                }}
              />
            )}
            type="secondary"
            size="small"
            caret={isOpen ? 'up' : 'down'}
            onClick={(event: React.SyntheticEvent) => {
              setShowTooltip(false);

              if (isOpen) {
                hide();
              } else {
                handleTargetClick(event);
              }
            }}
            buttonAttributes={{
              ref: buttonRef,
              onMouseEnter: () => setShowTooltip(true),
              onMouseLeave: () => setShowTooltip(false),
            }}
          />
        </div>
      </Tooltip>
    );

    const menuAttributes = {
      role: 'listbox',
      'aria-label': 'Font colour',
    };

    return (
      <Menu
        elevated
        ref={ref}
        spacing={theme.spacing.xxSmall}
        menuListComponent="ul"
        menuListAttributes={menuAttributes}
        ariaLabelledby="font-colour-type"
        renderTarget={renderButton}
        renderChildren={renderMenuItems}
        verticalSwitchThreshold={250}
        css={{ zIndex: 1500 }}
        disableAutoFocus
      />
    );
  },
);

TextColourButton.displayName = 'TextColourButton';
